/**
 * VIP Payment Configuration
 * Control VIP access availability and capacity settings
 */

import { isVipCustomPaymentsEnabled, isVipPlanCardEnabled } from "./featureFlags";

export interface VipConfig {
  // Global VIP availability (from feature flags or env)
  isAvailable: boolean;
  
  // Optional: Per-user VIP availability (can be extended with API)
  isAvailableForUser?: (userId?: string) => boolean;
  
  // Optional: Capacity settings
  maxCapacity?: number;
  currentCapacity?: number;
  
  // Optional: Custom messages
  unavailableMessage?: {
    es: string;
    en: string;
  };
}

/**
 * Get VIP custom payments availability (/vip/:nombre)
 * Can be extended to check API, database, or other sources
 */
export const getVipCustomPaymentsAvailability = async (): Promise<boolean> => {
  // Check environment variable first (highest priority)
  const envValue = import.meta.env.VITE_VIP_CUSTOM_PAYMENTS_ENABLED;
  if (envValue !== undefined) {
    return envValue === 'true' || envValue === '1';
  }
  
  // Fall back to feature flag
  return isVipCustomPaymentsEnabled();
};

/**
 * Synchronous version for VIP custom payments
 */
export const isVipCustomPaymentsAvailable = (): boolean => {
  // Check environment variable first
  const envValue = import.meta.env.VITE_VIP_CUSTOM_PAYMENTS_ENABLED;
  if (envValue !== undefined) {
    return envValue === 'true' || envValue === '1';
  }
  
  // Fall back to feature flag
  return isVipCustomPaymentsEnabled();
};

/**
 * Check if VIP plan card should be shown (BioTemplatePremium & AR Invitacion)
 */
export const isVipPlanCardAvailable = (): boolean => {
  // Check environment variable first
  const envValue = import.meta.env.VITE_VIP_PLAN_CARD_ENABLED;
  if (envValue !== undefined) {
    return envValue === 'true' || envValue === '1';
  }
  
  // Fall back to feature flag
  return isVipPlanCardEnabled();
};

// Backward compatibility
export const getVipAvailability = getVipCustomPaymentsAvailability;
export const isVipAvailable = isVipCustomPaymentsAvailable;

/**
 * Get VIP configuration
 * Centralized place to manage all VIP settings
 */
export const getVipConfig = (): VipConfig => {
  return {
    isAvailable: isVipAvailable(),
    unavailableMessage: {
      es: "El acceso VIP ha alcanzado su capacidad máxima. Este es un espacio exclusivo y limitado.",
      en: "VIP access has reached its maximum capacity. This is an exclusive and limited space.",
    },
  };
};

// Export default config
export default getVipConfig();
